import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";
import { getSubscriptionLimits } from "@/utils/subscriptionUtils";

export const useIncognitoBrowsing = () => {
  const { user } = useAuth();
  const { userProfile } = useSubscriptionStatus();
  const [isIncognitoEnabled, setIsIncognitoEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  // Check if user has incognito browsing permission
  const currentTier = userProfile?.subscription_tier || 'alpha200';
  const subscriptionLimits = getSubscriptionLimits(currentTier as any);
  const hasIncognitoAccess = subscriptionLimits.incognitoBrowsing;

  // Load incognito preference from user profile
  useEffect(() => {
    const loadIncognitoPreference = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('additional_info')
          .eq('user_id', user.id)
          .single();

        if (error) throw error;

        // Parse additional_info JSON to get incognito preference with safe fallback
        let additionalInfo: any = {};
        if (data?.additional_info) {
          try {
            // Handle both string and object cases
            if (typeof data.additional_info === 'string') {
              additionalInfo = JSON.parse(data.additional_info);
            } else {
              additionalInfo = data.additional_info;
            }
          } catch (parseError) {
            console.warn('Failed to parse additional_info, using empty object:', parseError);
            additionalInfo = {};
          }
        }
        setIsIncognitoEnabled(additionalInfo.incognitoBrowsing === true);
      } catch (error) {
        console.error('Error loading incognito preference:', error);
        setIsIncognitoEnabled(false); // Safe fallback
      } finally {
        setLoading(false);
      }
    };

    loadIncognitoPreference();
  }, [user]);

  // Update incognito preference
  const toggleIncognito = async (enabled: boolean) => {
    if (!user || !hasIncognitoAccess) return false;

    try {
      // Get current additional_info
      const { data: currentData, error: fetchError } = await supabase
        .from('user_profiles')
        .select('additional_info')
        .eq('user_id', user.id)
        .single();

      if (fetchError) throw fetchError;

      // Update additional_info with incognito preference with safe parsing
      let currentInfo: any = {};
      if (currentData?.additional_info) {
        try {
          if (typeof currentData.additional_info === 'string') {
            currentInfo = JSON.parse(currentData.additional_info);
          } else {
            currentInfo = currentData.additional_info;
          }
        } catch (parseError) {
          console.warn('Failed to parse existing additional_info, using empty object:', parseError);
          currentInfo = {};
        }
      }
      const updatedInfo = {
        ...currentInfo,
        incognitoBrowsing: enabled
      };

      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({ 
          additional_info: JSON.stringify(updatedInfo)
        })
        .eq('user_id', user.id);

      if (updateError) throw updateError;

      setIsIncognitoEnabled(enabled);
      return true;
    } catch (error) {
      console.error('Error updating incognito preference:', error);
      return false;
    }
  };

  return {
    isIncognitoEnabled,
    hasIncognitoAccess,
    toggleIncognito,
    loading
  };
};